/**
 * Wake-only quick-command intent parser.
 *
 * Given a raw transcript, matchIntent(text, ctx) tries each pattern in order
 * and, if any matches, dispatches to a direct API call and returns a spoken
 * response string (short, TTS-friendly). Returns null on no match so the caller
 * can fall through to the full /companion/chat pipeline.
 *
 * Every handler MUST resolve to a spoken string (never throw) so Forge always
 * says something back to the user, even on API failure.
 */
import { api } from "./api";


function _fmtUsd(n) {
  if (n == null || Number.isNaN(n)) return "zero dollars";
  const sign = n < 0 ? "negative " : "";
  const val = Math.abs(n) >= 100
    ? Math.abs(n).toFixed(0)
    : Math.abs(n).toFixed(2);
  return `${sign}${val} dollars`;
}


function _fmtSpokenSymbol(s) {
  // "EURUSD" -> "E U R U S D" so TTS pronounces it letter-by-letter for forex,
  // but keep it as-is for well-known tickers like BTC or AAPL.
  const clean = String(s || "").toUpperCase();
  if (clean.length === 6 && !/[0-9]/.test(clean)) {
    return `${clean.slice(0, 3)} ${clean.slice(3)}`;   // "EUR USD"
  }
  return clean;
}


// ---------- individual intents ----------

async function _pnlBalance() {
  const { data } = await api.get("/portfolio/summary");
  if (!data.account_count) {
    return "You don't have any broker accounts connected yet. Head to the Broker page to link one.";
  }
  const c = data.combined || {};
  const pnl = _fmtUsd(c.open_pnl);
  const eq  = _fmtUsd(c.equity);
  const bal = _fmtUsd(c.balance);
  return `Across ${data.account_count} account${data.account_count === 1 ? "" : "s"}, your open P and L is ${pnl}. Equity ${eq}, balance ${bal}.`;
}


async function _priceLookup(symbol) {
  try {
    const { data } = await api.get(`/market/quote/${symbol}`);
    const t = data.today || {};
    return `${_fmtSpokenSymbol(symbol)} is at ${t.close?.toFixed(5) ?? "unknown"}. Today's high ${t.high?.toFixed(5) ?? "n/a"}, low ${t.low?.toFixed(5) ?? "n/a"}.`;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) return `I don't have live data for ${_fmtSpokenSymbol(symbol)} yet. Connect a broker or set an Alpha Vantage key.`;
    return `Couldn't fetch the price for ${_fmtSpokenSymbol(symbol)}.`;
  }
}


async function _pauseOrResume(symbolOrName, targetStatus) {
  const { data: deps } = await api.get("/deployments");
  const needle = String(symbolOrName || "").toLowerCase();
  const match = (deps || []).find((d) => {
    const sym = String(d.symbol || "").toLowerCase();
    const name = String(d.strategy_name || d.name || "").toLowerCase();
    return sym === needle || name.includes(needle) || sym.includes(needle);
  });
  if (!match) {
    return `I couldn't find a deployment matching ${symbolOrName}. Try the exact symbol or strategy name.`;
  }
  const label = _fmtSpokenSymbol(match.symbol || match.strategy_name || "the bot");
  if (match.status === targetStatus) {
    return `${label} is already ${targetStatus === "running" ? "running" : "paused"}.`;
  }
  await api.patch(`/deployments/${match.deployment_id}`, { status: targetStatus });
  return targetStatus === "paused" ? `Paused ${label}.` : `Resumed ${label}. Trading is back on.`;
}


async function _alertsSummary() {
  const { data } = await api.get("/notifications", { params: { unread_only: true, limit: 5 } });
  const items = data.items || data.notifications || [];
  if (!items.length) return "You have no unread alerts.";
  const latest = items[0];
  return `${items.length} unread alert${items.length === 1 ? "" : "s"}. Latest: ${latest.title || latest.body || "check the bell"}.`;
}


// ---------- intent table (order matters, most specific first) ----------

const INTENTS = [
  {
    id: "pnl_balance",
    // matches "pnl", "profit", "p and l", "balance", "equity", "how much am I up/down"
    re: /\b(p\.?n\.?l\.?|profit|p and l|balance|equity|how much (am i|are we) (up|down))\b/i,
    handler: () => _pnlBalance(),
  },
  {
    id: "price",
    // "price of EURUSD", "how is BTCUSD", "quote on AAPL", "what is EURUSD at"
    re: /\b(?:price of|quote on|how (?:is|are)|what(?:'s| is) (?:the price of )?)?\s*([A-Z]{3,10})\b(?:\s*(?:doing|trading|going|at))?/i,
    // secondary trigger word so we don't grab a stray symbol from any sentence
    guard: /\b(price|quote|how is|how are|what.?s [a-z]* at|doing|trading)\b/i,
    handler: (m) => _priceLookup(m[1]),
  },
  {
    id: "pause",
    // "pause EURUSD", "pause the EURUSD bot", "pause my rsi strategy"
    re: /\bpause(?:\s+the|\s+my)?\s+([A-Za-z0-9 ]+?)(?:\s+(?:bot|strategy|ea))?\s*$/i,
    handler: (m) => _pauseOrResume(m[1].trim(), "paused"),
  },
  {
    id: "resume",
    re: /\b(?:resume|start|unpause)(?:\s+the|\s+my)?\s+([A-Za-z0-9 ]+?)(?:\s+(?:bot|strategy|ea))?\s*$/i,
    handler: (m) => _pauseOrResume(m[1].trim(), "running"),
  },
  {
    id: "alerts",
    re: /\b(any (?:alerts|notifications|updates)|read my alerts|whats new)\b/i,
    handler: () => _alertsSummary(),
  },
];


/** Try every registered intent in order. Returns the spoken response
 *  or null if nothing matched. Guarantees a string reply on any match
 *  (handlers wrap their own errors). */
export async function matchIntent(transcript) {
  if (!transcript || typeof transcript !== "string") return null;
  const t = transcript.trim();
  for (const it of INTENTS) {
    const m = it.re.exec(t);
    if (!m) continue;
    if (it.guard && !it.guard.test(t)) continue;
    try {
      const reply = await it.handler(m);
      return { intent: it.id, reply: String(reply || "Done.") };
    } catch (err) {
      console.debug(`[intent:${it.id}] handler threw`, err);
      return { intent: it.id, reply: "Something went wrong. Try again in a moment." };
    }
  }
  return null;
}


// Exposed for tests
export const _INTENTS_FOR_TESTS = INTENTS;
